import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { formatDistanceToNow } from 'date-fns';
import { useGetUserById, useCheckUserLogin } from '../../hooks/use-user';
import { useAuthStore } from '../../stores';
import { Heart, Clock, CheckCircle } from 'lucide-react';
import { getArticlePath } from '../../utils/slug';

export function ArticleCard({ article, horizontal = false }) {
  const { walletAddress, isAuthenticated } = useAuthStore();
  const { data: author, isLoading: isLoadingAuthor } = useGetUserById(
    article?.creator
  );
  const { data: isLoggedIn } = useCheckUserLogin();

  const formattedDate = article.createdAt
    ? formatDistanceToNow(new Date(article.createdAt), { addSuffix: true })
    : 'recently';

  // Estimate reading time based on content length
  const getReadingTime = () => {
    const text = article.text || article.description || '';
    const wordCount = text.split(/\s+/).length;
    const readingTime = Math.max(1, Math.ceil(wordCount / 200)); // Assuming 200 words per minute
    return `${readingTime} min read`;
  };

  // Get topic colors if available
  const getTopicColor = (topicName) => {
    const colors = [
      '#3B82F6', // blue
      '#10B981', // green
      '#8B5CF6', // purple
      '#EC4899', // pink
      '#F97316', // orange
      '#EF4444', // red
      '#6366F1', // indigo
      '#14B8A6', // teal
      '#F59E0B', // amber
    ];

    let hash = 0;
    for (let i = 0; i < topicName.length; i++) {
      hash = topicName.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Link to={getArticlePath(article)} className="block group h-full">
      <Card className="h-full overflow-hidden transition-all duration-300 border-0 bg-transparent hover:bg-transparent">
        <CardContent className="p-0 h-full flex flex-col">
          {/* Image Section */}
          <div className="relative w-full aspect-video overflow-hidden rounded-t-3xl">
            <img
              src={
                article.coverImage ||
                article.image ||
                '/abstract-article-cover.png' ||
                '/placeholder.svg'
              }
              alt={article.title || article.description || 'Article'}
              className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
            />

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

            {/* Mint Information Badge */}
            {article.mintInformation && (
              <div className="absolute top-3 right-3 bg-black/60 group-hover:bg-violet-500/20 text-white group-hover:text-violet-200 text-xs px-2.5 py-1 rounded-full backdrop-blur-md transition-all border border-white/10 group-hover:border-violet-500/30">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="h-3 w-3" />
                  <span className="hidden sm:inline font-medium">
                    {article.mintInformation.chainName}
                  </span>
                  <span className="sm:hidden font-medium">Verified</span>
                </div>
              </div>
            )}

            {/* Topics overlay */}
            {Array.isArray(article.topics) && article.topics.length > 0 && (
              <div className="absolute bottom-3 right-3 flex gap-1 items-center">
                {article.topics.slice(0, 3).map((topic, idx) => {
                  let topicNames = [];
                  if (typeof topic === 'string') {
                    topicNames = topic.split(',').map((t) => t.trim());
                  } else if (topic?.slug || topic?.name) {
                    topicNames = [(topic.slug || topic.name).trim()];
                  }
                  return topicNames.slice(0, 1).map(
                    (name) =>
                      name && (
                        <Badge
                          key={name + idx}
                          variant="outline"
                          className="text-xs px-2.5 py-1 border-white/10 font-medium backdrop-blur-md whitespace-nowrap bg-black/40 text-white group-hover:border-white/20"
                        >
                          {name}
                        </Badge>
                      )
                  );
                })}
                {Array.isArray(article.topics) && article.topics.length > 3 && (
                  <Badge
                    variant="outline"
                    className="text-xs px-2 py-0.5 bg-black/70 text-white border-0 backdrop-blur-sm whitespace-nowrap"
                  >
                    +{article.topics.length - 3}
                  </Badge>
                )}
              </div>
            )}

            {/* Reading time badge */}
            <div className="absolute top-3 left-3 bg-black/60 group-hover:bg-cyan-500/20 text-zinc-300 group-hover:text-cyan-200 text-xs px-2.5 py-1 rounded-full backdrop-blur-md transition-all border border-white/10 group-hover:border-cyan-500/30">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                <span className="font-medium">{getReadingTime()}</span>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-5 sm:p-6 flex flex-col flex-1">
            {/* Author and Date Row */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Avatar className="h-8 w-8 border border-white/10 ring-2 ring-transparent group-hover:ring-violet-500/30 transition-all">
                  <AvatarImage
                    src={author?.avatar || '/placeholder.svg'}
                    alt={article.creator || 'Unknown'}
                  />
                  <AvatarFallback className="text-xs bg-zinc-800 text-zinc-400">
                    {(article.creator || 'U').toString().charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-white group-hover:text-violet-400 truncate transition-colors">
                      {author?.username || 'Unknown'}
                    </span>
                    <span className="text-zinc-600 hidden sm:inline">•</span>
                    <span className="text-zinc-500 text-xs hidden sm:inline">
                      {formattedDate}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 sm:hidden mt-0.5">
                    {formattedDate}
                  </div>
                </div>
              </div>
            </div>

            {/* UAL indicator */}
            {article.ual && (
              <div className="mb-3">
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 group-hover:text-cyan-400 bg-white/5 group-hover:bg-cyan-500/10 px-2 py-1 rounded-md transition-colors w-fit border border-white/5 group-hover:border-cyan-500/20">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="18" height="18" x="3" y="3" rx="2" />
                    <path d="M7 7h.01" />
                    <path d="M17 7h.01" />
                    <path d="M7 17h.01" />
                    <path d="M17 17h.01" />
                  </svg>
                  <span className="break-all font-mono">
                    {article.ual.substring(0, 20)}...
                  </span>
                </div>
              </div>
            )}

            {/* Title and Subtitle */}
            <div className="mb-3">
              <h3 className="text-lg sm:text-xl font-bold mb-2 line-clamp-2 text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-zinc-400 transition-all">
                {article.title || 'Untitled'}
              </h3>
              {article.subtitle && (
                <h4 className="text-sm text-zinc-400 group-hover:text-zinc-300 line-clamp-1 mb-2">
                  {article.subtitle}
                </h4>
              )}
            </div>

            {/* Description */}
            <p className="text-zinc-400 group-hover:text-zinc-300 text-sm line-clamp-2 mb-6 leading-relaxed flex-1">
              {article.description ||
                article.text?.replace(/<[^>]+>/g, '').substring(0, 120) ||
                'No description available.'}
            </p>

            {/* Footer with topics and read more */}
            <div className="flex items-center justify-between pt-4 border-t border-white/5 mt-auto">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 text-zinc-500 group-hover:text-red-400 transition-colors">
                  <Heart className="h-4 w-4" />
                  <span className="text-xs font-medium">
                    {article.likes || 0}
                  </span>
                </div>
              </div>

              {/* Read more indicator */}
              <div className="flex items-center gap-1 text-xs font-medium text-zinc-500 group-hover:text-white transition-colors">
                Read Article
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="group-hover:translate-x-0.5 transition-transform"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
